// src/Dashboard/pages/teacher/PrepaPhysique/components/AssignmentCreateDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import {
  FaCheck,
  FaDumbbell,
  FaFilter,
  FaLayerGroup,
  FaPlus,
  FaSave,
  FaSearch,
  FaTimes,
  FaUserFriends,
} from "react-icons/fa";

import {
  AGE_CATEGORIES,
  STOPPED_STATUSES,
  getAdherentName,
  getBirthYear,
  getGroupsLabel,
  getPhysicalCategories,
  uniqSorted,
} from "../utils/prepaPhysiqueUtils";

const initialForm = {
  prepa_physique_id: "",
  date_realisation: new Date().toISOString().slice(0, 10),
  repetitions: "",
  is_done: false,
  notify: true,

  search: "",
  season: "",
  groupId: "",
  ageCategory: "",
  birthYear: "",
  onlyLicensed: true,
  excludeAlreadyAssigned: true,
};

export default function AssignmentCreateDrawer({
  isOpen,
  presetExercise,
  exercises = [],
  adherents = [],
  groups = [],
  seasons = [],
  existingAssignments = [],
  saving = false,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState(initialForm);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const selectedExerciseId = String(form.prepa_physique_id || "");

  useEffect(() => {
    if (!isOpen) return;

    const defaultSeason = seasons?.[1] || seasons?.[0] || "";

    setForm({
      ...initialForm,
      prepa_physique_id: presetExercise?.id ? String(presetExercise.id) : "",
      season: defaultSeason,
    });

    setSelectedIds(new Set());

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, presetExercise, seasons, onClose]);

  const groupNameToId = useMemo(() => {
    const map = new Map();

    groups.forEach((group) => {
      const key = String(group?.nom || "").trim().toLowerCase();
      if (key) map.set(key, String(group.id));
    });

    return map;
  }, [groups]);

  const alreadyAssignedIds = useMemo(() => {
    if (!selectedExerciseId) return new Set();

    return new Set(
      (existingAssignments || [])
        .filter(
          (item) =>
            String(item?.prepa_physique_id) === String(selectedExerciseId)
        )
        .map((item) => String(item?.adherent_id))
        .filter(Boolean)
    );
  }, [existingAssignments, selectedExerciseId]);

  const birthYears = useMemo(() => {
    return uniqSorted(
      (adherents || [])
        .map((adherent) => getBirthYear(adherent?.date_naissance))
        .filter(Boolean)
    );
  }, [adherents]);

  const filteredAdherents = useMemo(() => {
    const query = String(form.search || "").trim().toLowerCase();

    return (adherents || [])
      .filter((adherent) => {
        if (form.excludeAlreadyAssigned && alreadyAssignedIds.has(String(adherent.id))) {
          return false;
        }

        if (form.onlyLicensed) {
          if (adherent?.statut_inscription !== "Licencié") return false;
          if (STOPPED_STATUSES.includes(adherent?.statut_inscription)) return false;
        }

        if (form.season && adherent?.saison_active !== form.season) {
          return false;
        }

        if (form.groupId) {
          const adherentGroups = Array.isArray(adherent?.Groups)
            ? adherent.Groups
            : [];

          const hasGroup = adherentGroups.some((group) => {
            if (group?.id != null) {
              return String(group.id) === String(form.groupId);
            }

            const key = String(group?.nom || "").trim().toLowerCase();
            return groupNameToId.get(key) === String(form.groupId);
          });

          if (!hasGroup) return false;
        }

        const physical = getPhysicalCategories(adherent, form.season);

        if (form.ageCategory && physical.ageCategory !== form.ageCategory) {
          return false;
        }

        if (form.birthYear && String(physical.birthYear) !== String(form.birthYear)) {
          return false;
        }

        if (!query) return true;

        return [
          adherent?.nom,
          adherent?.prenom,
          adherent?.licence_number,
          adherent?.statut_inscription,
          getGroupsLabel(adherent),
          physical.ageCategory,
          physical.birthYear,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query);
      })
      .sort((a, b) => getAdherentName(a).localeCompare(getAdherentName(b), "fr"));
  }, [adherents, form, alreadyAssignedIds, groupNameToId]);

  const selectedAdherents = useMemo(() => {
    const map = new Map((adherents || []).map((adherent) => [String(adherent.id), adherent]));

    return Array.from(selectedIds)
      .map((id) => map.get(String(id)))
      .filter(Boolean)
      .sort((a, b) => getAdherentName(a).localeCompare(getAdherentName(b), "fr"));
  }, [adherents, selectedIds]);

  const toggleId = (id) => {
    const key = String(id);

    setSelectedIds((previous) => {
      const next = new Set(previous);

      if (next.has(key)) next.delete(key);
      else next.add(key);

      return next;
    });
  };

  const selectFiltered = () => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      filteredAdherents.forEach((adherent) => next.add(String(adherent.id)));
      return next;
    });
  };

  const unselectFiltered = () => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      filteredAdherents.forEach((adherent) => next.delete(String(adherent.id)));
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    onSubmit?.({
      adherentIds: Array.from(selectedIds).map(Number),
      prepa_physique_id: Number(form.prepa_physique_id),
      date_realisation: form.date_realisation,
      repetitions: form.repetitions,
      is_done: form.is_done,
      notify: form.notify,
    });
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[100]">
      <div
        className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute inset-y-0 right-0 flex w-full max-w-6xl flex-col overflow-hidden bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-800 sm:px-6">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Affectation par lot
            </p>

            <h2 className="truncate text-lg font-bold text-slate-900 dark:text-white">
              Assigner un exercice
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="grid gap-5 xl:grid-cols-[390px_1fr]">
              <div className="space-y-5">
                <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
                  <div className="mb-4 flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                      <FaDumbbell />
                    </span>

                    <div>
                      <h3 className="font-bold text-slate-900 dark:text-white">
                        Exercice à affecter
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        Choisissez l’exercice et les consignes.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <SelectField
                      label="Exercice *"
                      value={form.prepa_physique_id}
                      onChange={(value) =>
                        setForm((previous) => ({
                          ...previous,
                          prepa_physique_id: value,
                        }))
                      }
                      required
                    >
                      <option value="">Sélectionner un exercice</option>
                      {exercises.map((exercise) => (
                        <option key={exercise.id} value={exercise.id}>
                          {exercise.nom}
                          {exercise.programme ? ` · ${exercise.programme}` : ""}
                        </option>
                      ))}
                    </SelectField>

                    <div className="grid grid-cols-2 gap-3">
                      <Field
                        label="Date prévue"
                        type="date"
                        value={form.date_realisation}
                        onChange={(value) =>
                          setForm((previous) => ({
                            ...previous,
                            date_realisation: value,
                          }))
                        }
                        required
                      />

                      <Field
                        label="Répétitions"
                        type="number"
                        min="0"
                        value={form.repetitions}
                        onChange={(value) =>
                          setForm((previous) => ({
                            ...previous,
                            repetitions: value,
                          }))
                        }
                        placeholder="Ex : 30"
                      />
                    </div>

                    <label className="flex items-center gap-3 rounded-2xl bg-white px-3 py-2 text-sm font-bold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-700">
                      <input
                        type="checkbox"
                        checked={form.is_done}
                        onChange={(event) =>
                          setForm((previous) => ({
                            ...previous,
                            is_done: event.target.checked,
                          }))
                        }
                        className="h-5 w-5 accent-indigo-600"
                      />
                      Déjà effectué
                    </label>

                    <label className="flex items-center gap-3 rounded-2xl bg-white px-3 py-2 text-sm font-bold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-700">
                      <input
                        type="checkbox"
                        checked={form.notify}
                        onChange={(event) =>
                          setForm((previous) => ({
                            ...previous,
                            notify: event.target.checked,
                          }))
                        }
                        className="h-5 w-5 accent-indigo-600"
                      />
                      Notifier les adhérents
                    </label>
                  </div>
                </div>

                <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/60">
                  <div className="mb-4 flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                      <FaFilter />
                    </span>

                    <div>
                      <h3 className="font-bold text-slate-900 dark:text-white">
                        Ciblage des adhérents
                      </h3>
                      <p className="text-sm text-slate-600 dark:text-slate-300">
                        Filtrez par saison, groupe, âge ou année.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="relative">
                      <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

                      <input
                        type="text"
                        value={form.search}
                        onChange={(event) =>
                          setForm((previous) => ({
                            ...previous,
                            search: event.target.value,
                          }))
                        }
                        placeholder="Rechercher un adhérent..."
                        className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                      />
                    </div>

                    <SelectField
                      label="Saison"
                      value={form.season}
                      onChange={(value) =>
                        setForm((previous) => ({
                          ...previous,
                          season: value,
                        }))
                      }
                    >
                      <option value="">Toutes les saisons</option>
                      {seasons.map((season) => (
                        <option key={season} value={season}>
                          {season}
                        </option>
                      ))}
                    </SelectField>

                    <SelectField
                      label="Groupe"
                      value={form.groupId}
                      onChange={(value) =>
                        setForm((previous) => ({
                          ...previous,
                          groupId: value,
                        }))
                      }
                    >
                      <option value="">Tous les groupes</option>
                      {groups.map((group) => (
                        <option key={group.id} value={group.id}>
                          {group.nom}
                        </option>
                      ))}
                    </SelectField>

                    <div className="grid grid-cols-2 gap-3">
                      <SelectField
                        label="Catégorie"
                        value={form.ageCategory}
                        onChange={(value) =>
                          setForm((previous) => ({
                            ...previous,
                            ageCategory: value,
                          }))
                        }
                      >
                        <option value="">Toutes</option>
                        {AGE_CATEGORIES.map((category) => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </SelectField>

                      <SelectField
                        label="Année"
                        value={form.birthYear}
                        onChange={(value) =>
                          setForm((previous) => ({
                            ...previous,
                            birthYear: value,
                          }))
                        }
                      >
                        <option value="">Toutes</option>
                        {birthYears.map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                      </SelectField>
                    </div>

                    <label className="flex items-center gap-3 rounded-2xl bg-slate-50 px-3 py-2 text-sm font-bold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700">
                      <input
                        type="checkbox"
                        checked={form.onlyLicensed}
                        onChange={(event) =>
                          setForm((previous) => ({
                            ...previous,
                            onlyLicensed: event.target.checked,
                          }))
                        }
                        className="h-5 w-5 accent-emerald-600"
                      />
                      Licenciés uniquement
                    </label>

                    <label className="flex items-center gap-3 rounded-2xl bg-slate-50 px-3 py-2 text-sm font-bold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700">
                      <input
                        type="checkbox"
                        checked={form.excludeAlreadyAssigned}
                        onChange={(event) =>
                          setForm((previous) => ({
                            ...previous,
                            excludeAlreadyAssigned: event.target.checked,
                          }))
                        }
                        className="h-5 w-5 accent-emerald-600"
                      />
                      Exclure ceux déjà affectés
                    </label>
                  </div>
                </div>
              </div>

              <div className="min-w-0 space-y-5">
                <div className="grid gap-3 sm:grid-cols-3">
                  <StatBox
                    label="Filtrés"
                    value={filteredAdherents.length}
                    tone="indigo"
                  />

                  <StatBox
                    label="Sélectionnés"
                    value={selectedIds.size}
                    tone="emerald"
                  />

                  <StatBox
                    label="Déjà affectés"
                    value={alreadyAssignedIds.size}
                    tone="amber"
                  />
                </div>

                <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/60">
                  <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                      <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
                        <FaUserFriends />
                      </span>

                      <div>
                        <h3 className="font-bold text-slate-900 dark:text-white">
                          Adhérents disponibles
                        </h3>
                        <p className="text-sm text-slate-600 dark:text-slate-300">
                          Cochez les adhérents à qui affecter l’exercice.
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={selectFiltered}
                        className="inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-3 py-2 text-xs font-bold text-white transition hover:bg-indigo-700"
                      >
                        <FaCheck />
                        Tout filtré
                      </button>

                      <button
                        type="button"
                        onClick={unselectFiltered}
                        className="inline-flex items-center gap-2 rounded-2xl bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                      >
                        <FaTimes />
                        Retirer filtrés
                      </button>
                    </div>
                  </div>

                  <div className="max-h-[520px] overflow-auto pr-1">
                    {filteredAdherents.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
                        Aucun adhérent ne correspond aux filtres.
                      </div>
                    ) : (
                      <div className="grid gap-2 lg:grid-cols-2">
                        {filteredAdherents.map((adherent) => {
                          const selected = selectedIds.has(String(adherent.id));
                          const physical = getPhysicalCategories(adherent, form.season);

                          return (
                            <button
                              key={adherent.id}
                              type="button"
                              onClick={() => toggleId(adherent.id)}
                              className={`flex items-start gap-3 rounded-2xl border p-3 text-left transition ${
                                selected
                                  ? "border-indigo-200 bg-indigo-50 ring-1 ring-indigo-200 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:ring-indigo-500/20"
                                  : "border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:hover:bg-slate-900"
                              }`}
                            >
                              <span
                                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                                  selected
                                    ? "border-indigo-600 bg-indigo-600 text-white"
                                    : "border-slate-300 dark:border-slate-600"
                                }`}
                              >
                                {selected && <FaCheck className="text-[10px]" />}
                              </span>

                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
                                  {getAdherentName(adherent)}
                                </p>

                                <div className="mt-1 flex flex-wrap gap-1">
                                  <Tag>{adherent?.statut_inscription || "Sans statut"}</Tag>
                                  <Tag>{physical.ageCategory}</Tag>
                                  <Tag>{physical.birthYear || "Année inconnue"}</Tag>
                                  <Tag>{physical.weightCategory}</Tag>
                                </div>

                                <p className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
                                  <FaLayerGroup className="mr-1 inline" />
                                  {getGroupsLabel(adherent) || "Aucun groupe"}
                                </p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/60">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h3 className="font-bold text-slate-900 dark:text-white">
                      Sélection finale
                    </h3>

                    {selectedIds.size > 0 && (
                      <button
                        type="button"
                        onClick={clearSelection}
                        className="inline-flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-xs font-bold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-50 dark:bg-slate-950 dark:text-slate-200 dark:ring-slate-700"
                      >
                        <FaTimes />
                        Vider
                      </button>
                    )}
                  </div>

                  {selectedAdherents.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400">
                      Aucun adhérent sélectionné.
                    </div>
                  ) : (
                    <div className="flex max-h-40 flex-wrap gap-2 overflow-auto">
                      {selectedAdherents.map((adherent) => (
                        <button
                          key={adherent.id}
                          type="button"
                          onClick={() => toggleId(adherent.id)}
                          className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-700 ring-1 ring-indigo-200 transition hover:bg-indigo-100 dark:bg-indigo-900/20 dark:text-indigo-200 dark:ring-indigo-800/60"
                        >
                          {getAdherentName(adherent)}
                          <FaTimes size={10} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 bg-white/95 p-4 dark:border-slate-800 dark:bg-slate-900/95 sm:px-6">
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={saving || !form.prepa_physique_id || selectedIds.size === 0}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-3 text-sm font-bold text-white transition hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaSave />
                {saving
                  ? "Affectation..."
                  : `Affecter ${selectedIds.size || ""}`.trim()}
              </button>
            </div>
          </div>
        </form>
      </aside>
    </div>,
    document.body
  );
}

function Field({ label, type = "text", value, onChange, placeholder, required, min }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </label>

      <input
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
        min={min}
        placeholder={placeholder}
        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
      />
    </div>
  );
}

function SelectField({ label, value, onChange, children, required }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </label>

      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
      >
        {children}
      </select>
    </div>
  );
}

function StatBox({ label, value, tone }) {
  const className =
    tone === "emerald"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300"
      : tone === "amber"
      ? "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300"
      : "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300";

  return (
    <div className={`rounded-2xl border px-4 py-3 ${className}`}>
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] opacity-75">
        {label}
      </p>
      <p className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">
        {value}
      </p>
    </div>
  );
}

function Tag({ children }) {
  return (
    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">
      {children}
    </span>
  );
}